<script>
import { untrack } from "svelte";
import { Group, Image, Rect } from "svelte-konva";
import { viewportState } from "@app/lib/timeline/viewport.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { peaksState, refreshPeaks } from "@app/lib/timeline/peaksState.svelte.js";
import { renderWaveformTile } from "@app/lib/timeline/waveformImage.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";

/**
 * Waveform layer for the Konva timeline.
 *
 * Reads peaks from the shared `peaksState` store and renders them into an
 * offscreen canvas tile sized to cover the visible viewport plus a buffer.
 * As the user scrolls/zooms we re-render the tile only when the visible
 * window leaves the current tile's coverage area or the zoom changes -- this
 * bounds canvas memory regardless of media duration.
 *
 * @typedef {Object} Props
 * @property {number} y
 * @property {number} height
 */

/** @type {Props} */
let { y = 0, height = 80 } = $props();

const TILE_BUFFER_RATIO = 1.0;
const REDRAW_BUFFER_RATIO = 0.5;

let palette = $derived(getTimelinePalette(themeState.effective));

let tile = $state(null); // { startSec, endSec, pxPerSec, canvas, theme, peaksData, peaksSignature, durationSec, height, waveColor }

function regenerateTile({ visibleStartSec, visibleEndSec, pxPerSec, durationSec, waveColor, theme, peaksData, peaksSignature }) {
    if (!peaksData || !pxPerSec) return;
    const tileWidthSec = (visibleEndSec - visibleStartSec) * (1 + 2 * TILE_BUFFER_RATIO);
    const center = (visibleStartSec + visibleEndSec) / 2;
    const startSec = Math.max(0, center - tileWidthSec / 2);
    const endSec = durationSec
        ? Math.min(durationSec, startSec + tileWidthSec)
        : startSec + tileWidthSec;

    const canvas = renderWaveformTile({
        peaks: peaksData.data,
        pxPerSec,
        durationSec: durationSec || endSec,
        startSec,
        endSec,
        height,
        waveColor
    });
    if (!canvas) return;
    tile = { startSec, endSec, pxPerSec, canvas, theme, peaksData, peaksSignature, durationSec, height, waveColor };
}

function tileIsStale({
    visibleStartSec,
    visibleEndSec,
    pxPerSec,
    theme,
    peaksData,
    peaksSignature,
    durationSec,
    waveColor
}) {
    if (!tile) return true;
    if (tile.theme !== theme) return true;
    if (tile.pxPerSec !== pxPerSec) return true;
    if (tile.peaksData !== peaksData) return true;
    if (tile.peaksSignature !== peaksSignature) return true;
    if (tile.durationSec !== durationSec) return true;
    if (tile.height !== height) return true;
    if (tile.waveColor !== waveColor) return true;
    const buffer = (visibleEndSec - visibleStartSec) * REDRAW_BUFFER_RATIO;
    if (visibleStartSec < tile.startSec + buffer && tile.startSec > 0) return true;
    if (visibleEndSec > tile.endSec - buffer) return true;
    return false;
}

// React to viewport / peaks changes and refresh the tile when needed.
$effect(() => {
    const { pxPerSec, scrollX, containerWidth } = viewportState;
    const duration = playerState.duration || projectState?.media?.duration || projectState?.media?.info?.duration || 0;
    const theme = themeState.effective;
    const waveColor = palette.waveformColor;
    const peaksData = peaksState.data;
    const peaksSignature = peaksState.signature;
    const peaksLoading = peaksState.loading;
    if (!peaksData || !pxPerSec || !containerWidth) return;
    const visibleStartSec = scrollX / pxPerSec;
    const visibleEndSec = (scrollX + containerWidth) / pxPerSec;

    untrack(() => {
        if (peaksLoading) {
            if (!tile || tile.peaksSignature !== peaksSignature) tile = null;
            return;
        }
        if (tileIsStale({
            visibleStartSec,
            visibleEndSec,
            pxPerSec,
            theme,
            peaksData,
            peaksSignature,
            durationSec: duration,
            waveColor
        })) {
            regenerateTile({
                visibleStartSec,
                visibleEndSec,
                pxPerSec,
                durationSec: duration,
                waveColor,
                theme,
                peaksData,
                peaksSignature
            });
        }
    });
});

// React to media identity changes; (re)load peaks via the shared store.
$effect(() => {
    void projectState?.media?.peaksPath;
    void projectState?.media?.peaksData;
    void projectState?.media?.path;
    void projectState?.media?.localPath;
    void projectState?.media?.storage;
    refreshPeaks(projectState?.media, playerState.duration || 0);
});

let tileX = $derived(tile ? Math.round(tile.startSec * viewportState.pxPerSec - viewportState.scrollX) : 0);
let tileWidth = $derived(tile ? tile.canvas.width : 0);
</script>

<Group y={y} listening={false}>
    <Rect
        x={0}
        y={0}
        width={viewportState.containerWidth}
        height={height}
        fill={palette.waveformBg}
        listening={false}
        staticConfig={true} />

    {#if tile && tile.pxPerSec === viewportState.pxPerSec}
        <Image
            x={tileX}
            y={0}
            width={tileWidth}
            height={height}
            image={tile.canvas}
            listening={false} />
    {/if}
</Group>
